// ─────────────────────────────────────────────────────────────────────────────
// DEV-ONLY fake Supabase client for the Pools preview (localhost/?poolPreview).
//
// Everything lives in memory in this browser tab: no network calls, nothing is
// ever written to the real database, nobody's balance changes. Reload = reset.
// Only the query shapes the pool screens use are supported.
// ─────────────────────────────────────────────────────────────────────────────

import { LINEUP_FORMATS, defaultCap, NHL_CAP_MIN } from '../components/pools/poolFx';

export const PREVIEW_USER_ID = 'me';

const BOTS = [
  { id: 'u-habs', name: 'HabsFan99' },
  { id: 'u-snipe', name: 'SnipeCity' },
  { id: 'u-gloves', name: 'GlovesOff' },
  { id: 'u-shelf', name: 'TopShelf' },
  { id: 'u-luck', name: 'PuckLuck' },
  { id: 'u-five', name: 'FiveHole' },
];
const USERNAMES = { [PREVIEW_USER_ID]: 'CL34RY', ...Object.fromEntries(BOTS.map((b) => [b.id, b.name])) };

// Fallback players if the local roster API is unavailable.
const FALLBACK_PLAYERS = [
  ['Connor McDavid', 'EDM', 'F', 132], ['Nathan MacKinnon', 'COL', 'F', 118], ['Nikita Kucherov', 'TBL', 'F', 121],
  ['Leon Draisaitl', 'EDM', 'F', 106], ['Auston Matthews', 'TOR', 'F', 98], ['Cale Makar', 'COL', 'D', 92],
  ['Quinn Hughes', 'VAN', 'D', 90], ['Nick Suzuki', 'MTL', 'F', 89], ['Cole Caufield', 'MTL', 'F', 78],
  ['Lane Hutson', 'MTL', 'D', 66], ['Jack Hughes', 'NJD', 'F', 84], ['Kirill Kaprizov', 'MIN', 'F', 96],
  ['David Pastrnak', 'BOS', 'F', 101], ['Artemi Panarin', 'NYR', 'F', 99], ['Mitch Marner', 'TOR', 'F', 97],
  ['Sidney Crosby', 'PIT', 'F', 91], ['Igor Shesterkin', 'NYR', 'G', 0], ['Connor Hellebuyck', 'WPG', 'G', 0],
  ['Adam Fox', 'NYR', 'D', 74], ['Roman Josi', 'NSH', 'D', 71], ['Juraj Slafkovsky', 'MTL', 'F', 51],
  ['Macklin Celebrini', 'SJS', 'F', 63], ['Matthew Tkachuk', 'FLA', 'F', 88], ['Sam Reinhart', 'FLA', 'F', 86],
].map(([name, team, position, points], i) => ({ id: `fp-${i}`, name, team, position, points, capHit: Math.max(NHL_CAP_MIN, Math.round(points / 12) * 1000000 || 5000000) }));

const iso = (msFromNow) => new Date(Date.now() + msFromNow).toISOString();
const capOfId = (id, position) => (position === 'T' ? 0 : (players.find((x) => x.id === id)?.capHit ?? NHL_CAP_MIN));
const payroll = (poolId, userId) => db.pool_picks.filter((k) => k.pool_id === poolId && k.user_id === userId).reduce((sum, k) => sum + (k.cap_hit || 0), 0);
// hard cap: payroll + his cap hit + league minimum for every spot still to fill <= cap
// bots (anyMode) stay under the cap in penalty pools too — only a person chooses to go over
function capFitsFake(pool, userId, p, anyMode = false) {
  if (!pool?.cap_mode || (pool.cap_mode !== 'hard' && !anyMode) || p.position === 'T') return true;
  const perTeam = (pool.roster_size || 0) - (pool.team_round ? 1 : 0);
  const have = db.pool_picks.filter((k) => k.pool_id === pool.id && k.user_id === userId && k.position !== 'T').length;
  return payroll(pool.id, userId) + capOfId(p.id, p.position) + Math.max(0, perTeam - have - 1) * NHL_CAP_MIN <= pool.cap_amount;
}

// ── In-memory tables ─────────────────────────────────────────────────────────
const db = {
  pools: [],
  pool_members: [],
  pool_picks: [],
  pool_lineups: [],
  pool_transactions: [],
  pool_setting_changes: [],
  user_queue_templates: [],
  user_queue_entries: [],
};
let players = FALLBACK_PLAYERS;
let seq = 1;
const newId = (p) => `${p}-${seq++}`;

function member(poolId, userId, slot, extra = {}) {
  return {
    pool_id: poolId, user_id: userId, draft_slot: slot, score: 0,
    joined_at: iso(-86400000 + slot * 60000), quit_at: null, active_template_id: null,
    challenge_ready: false, challenge_reaction_ms: null, ...extra,
  };
}

function basePool(id, extra) {
  return {
    id, name: 'Pool', creator_id: PREVIEW_USER_ID, format: 'regular_season', status: 'lobby',
    entry_fee: 50, prize_pool: 0, max_members: 8, roster_size: 16, pick_seconds: 90,
    current_pick_index: 0, pick_deadline: null, pending_pick_player_id: null,
    pending_pick_player_name: null, pending_pick_team: null, pending_pick_position: null,
    challenge_state: 'none', challenge_armed_at: null, winner_id: null,
    slots_f: null, slots_d: null, slots_g: null, slots_t: null,
    created_at: iso(-3600000), ...extra,
  };
}

function seed() {
  Object.keys(db).forEach((k) => { db[k] = []; });

  // 1) Your open lobby (you're the creator) — shows Start Draft + reaction challenge
  db.pools.push(basePool('p-mine', { name: 'Boys Night Pool', entry_fee: 100, prize_pool: 400, max_members: 6, pick_seconds: 180, roster_size: 17, slots_f: 10, slots_d: 4, slots_g: 2, slots_t: 1, team_round: true, created_at: iso(-600000) }));
  [PREVIEW_USER_ID, 'u-habs', 'u-snipe', 'u-gloves'].forEach((u, i) => db.pool_members.push(member('p-mine', u, i + 1)));

  // 2) Someone else's open lobby you can join
  db.pools.push(basePool('p-join', { name: 'Playoff Beauties', creator_id: 'u-shelf', format: 'playoff', entry_fee: 250, prize_pool: 750, max_members: 8, created_at: iso(-1200000) }));
  ['u-shelf', 'u-luck', 'u-five'].forEach((u, i) => db.pool_members.push(member('p-join', u, i + 1)));

  // 3) A live draft — your pick is coming up
  db.pools.push(basePool('p-draft', { name: 'Friday Draft', status: 'drafting', entry_fee: 50, prize_pool: 200, max_members: 4, roster_size: 9, pick_seconds: 180, slots_f: 5, slots_d: 2, slots_g: 1, slots_t: 1, team_round: true, cap_mode: 'hard', cap_amount: defaultCap(8), current_pick_index: 1, pick_deadline: iso(180000), created_at: iso(-7200000) }));
  ['u-habs', 'u-snipe', PREVIEW_USER_ID, 'u-luck'].forEach((u, i) => db.pool_members.push(member('p-draft', u, i + 1)));

  // 4) An active season pool with standings
  db.pools.push(basePool('p-active', { name: 'Season Long Grind', status: 'active', creator_id: PREVIEW_USER_ID, entry_fee: 500, prize_pool: 2500, max_members: 5, roster_size: 12, slots_f: 7, slots_d: 3, slots_g: 1, slots_t: 1, team_round: true, lineup_mode: true, lineup_format: 'medium', free_agency: true, fa_pickups_per_month: 2, cap_mode: 'penalty', cap_amount: defaultCap(11), created_at: iso(-86400000 * 20) }));
  ['u-gloves', PREVIEW_USER_ID, 'u-shelf', 'u-habs', 'u-five'].forEach((u, i) => db.pool_members.push(member('p-active', u, i + 1)));

  // 5) A finished pool
  db.pools.push(basePool('p-done', { name: 'Last Year Champs', status: 'completed', creator_id: 'u-snipe', format: 'playoff', entry_fee: 100, prize_pool: 400, max_members: 4, winner_id: PREVIEW_USER_ID, created_at: iso(-86400000 * 200) }));
  ['u-snipe', PREVIEW_USER_ID, 'u-luck', 'u-gloves'].forEach((u, i) => db.pool_members.push(member('p-done', u, i + 1)));

  // A couple of saved pick lists
  const t1 = { id: 'tpl-1', user_id: PREVIEW_USER_ID, name: 'Habs First', created_at: iso(-86400000 * 3) };
  const t2 = { id: 'tpl-2', user_id: PREVIEW_USER_ID, name: 'Best Available', created_at: iso(-86400000) };
  db.user_queue_templates.push(t1, t2);
  const habs = players.filter((p) => p.team === 'MTL').slice(0, 5);
  const best = [...players].sort((a, b) => (b.points || 0) - (a.points || 0)).slice(0, 8);
  const addEntries = (tpl, list) => list.forEach((p, i) => db.user_queue_entries.push({
    id: newId('qe'), template_id: tpl.id, player_id: p.id, player_name: p.name, team: p.team, position: p.position, rank: i + 1,
  }));
  addEntries(t1, habs.length ? habs : best.slice(0, 5));
  addEntries(t2, best);
  db.pool_members.find((m) => m.pool_id === 'p-draft' && m.user_id === PREVIEW_USER_ID).active_template_id = 'tpl-2';

  // finished drafts for the active / completed pools, so every screen has real picks
  ['p-active', 'p-done'].forEach((id) => simulateDraft(db.pools.find((p) => p.id === id)));

  // a bot already used free agency this month (fills "Latest moves")
  const shelfF = db.pool_picks.filter((p) => p.pool_id === 'p-active' && p.user_id === 'u-shelf' && p.position === 'F')
    .sort((a, b) => (b.pick_number ?? 0) - (a.pick_number ?? 0))[0];
  const firstOfMonth = new Date(); firstOfMonth.setDate(1); firstOfMonth.setHours(10, 0, 0, 0);
  if (shelfF) swapPlayer(db.pools.find((p) => p.id === 'p-active'), 'u-shelf', shelfF, freeAgentsOf('p-active', 'F')[0], firstOfMonth.toISOString());
}

// undrafted players at a position, best first
function freeAgentsOf(poolId, position) {
  const taken = new Set(db.pool_picks.filter((k) => k.pool_id === poolId).map((k) => k.player_id));
  return players.filter((p) => p.position === position && !taken.has(p.id)).sort((a, b) => (b.points || 0) - (a.points || 0));
}

// drop `drop` (a pool_picks row), sign `add` (a player) into the same lineup spot
function swapPlayer(pool, userId, drop, add, at = new Date().toISOString()) {
  if (!pool || !drop || !add) return;
  const lu = db.pool_lineups.find((l) => l.pool_id === pool.id && l.player_id === drop.player_id);
  db.pool_picks = db.pool_picks.filter((p) => !(p.pool_id === pool.id && p.player_id === drop.player_id));
  db.pool_lineups = db.pool_lineups.filter((l) => !(l.pool_id === pool.id && l.player_id === drop.player_id));
  db.pool_picks.push({ id: newId('pk'), pool_id: pool.id, user_id: userId, pick_number: null, round: -1, player_id: add.id, player_name: add.name, team: add.team, position: add.position, cap_hit: capOfId(add.id, add.position), acquired_via: 'free_agent', acquired_at: at });
  if (pool.lineup_mode) db.pool_lineups.push({ pool_id: pool.id, user_id: userId, player_id: add.id, slot: lu ? lu.slot : add.position + 'X' });
  db.pool_transactions.push({ id: newId('tx'), pool_id: pool.id, user_id: userId, kind: 'free_agent', drop_player_id: drop.player_id, drop_player_name: drop.player_name, drop_team: drop.team, add_player_id: add.id, add_player_name: add.name, add_team: add.team, position: add.position, created_at: at });
}
function pickupsLeft(pool, userId) {
  const now = new Date();
  const used = db.pool_transactions.filter((x) => {
    const d = new Date(x.created_at);
    return x.pool_id === pool.id && x.user_id === userId && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }).length;
  let limit = pool.fa_pickups_per_month ?? 2;
  if (pool.cap_mode && payroll(pool.id, userId) > pool.cap_amount) limit = Math.min(limit, 1);
  return Math.max(0, limit - used);
}

// Team round (reverse order), then players snake from slot 1, respecting position slots
function simulateDraft(pool) {
  if (!pool) return;
  const mem = db.pool_members.filter((m) => m.pool_id === pool.id).sort((a, b) => a.draft_slot - b.draft_slot);
  const n = mem.length;
  const taken = new Set();
  const nhl = players.filter((p) => p.position === 'T').sort((a, b) => (b.points || 0) - (a.points || 0));
  const skaters = players.filter((p) => p.position !== 'T').sort((a, b) => (b.points || 0) - (a.points || 0));
  [...mem].reverse().forEach((m, i) => {
    const tm = nhl[(i * 3) % Math.max(1, nhl.length)];
    if (!tm) return;
    taken.add(tm.id);
    db.pool_picks.push({ id: newId('pk'), pool_id: pool.id, user_id: m.user_id, pick_number: null, round: 0, player_id: tm.id, player_name: tm.name, team: tm.team, position: 'T', cap_hit: 0 });
  });
  const perMember = (pool.roster_size || 7) - (pool.team_round ? 1 : 0);
  let pickNo = 1;
  for (let r = 0; r < perMember; r++) {
    const order = r % 2 === 0 ? mem : [...mem].reverse();
    for (const m of order) {
      const p = skaters.find((x) => !taken.has(x.id) && !positionFull(pool, m.user_id, x.position) && capFitsFake(pool, m.user_id, x, true));
      if (!p) continue;
      taken.add(p.id);
      db.pool_picks.push({ id: newId('pk'), pool_id: pool.id, user_id: m.user_id, pick_number: pickNo++, round: r + 1, player_id: p.id, player_name: p.name, team: p.team, position: p.position, cap_hit: capOfId(p.id, p.position) });
    }
  }
  if (pool.lineup_mode) initLineups(pool);
}

// Default lineup: draft order fills Line 1, Line 2, … then the extras (same as init_pool_lineups in SQL)
function initLineups(pool) {
  const slots = LINEUP_FORMATS[pool.lineup_format] || [];
  db.pool_members.filter((m) => m.pool_id === pool.id).forEach((m) => {
    const used = {};
    db.pool_picks
      .filter((p) => p.pool_id === pool.id && p.user_id === m.user_id && p.position !== 'T')
      .sort((a, b) => (a.pick_number ?? 999) - (b.pick_number ?? 999))
      .forEach((p) => {
        const slot = slots.find((sl) => sl.pos === p.position && (used[sl.id] || 0) < sl.cap);
        const id = slot ? slot.id : p.position + 'X';
        used[id] = (used[id] || 0) + 1;
        db.pool_lineups.push({ pool_id: pool.id, user_id: m.user_id, player_id: p.player_id, slot: id });
      });
  });
}

export function seedPlayers(list) {
  if (Array.isArray(list) && list.length > 20) players = list;
  seed();
}
seed();

// Fake standings for active/completed pools (replaces api.fetchPoolStandings)
export function fakeStandings(poolId) {
  const pool = db.pools.find((p) => p.id === poolId);
  const mem = db.pool_members.filter((m) => m.pool_id === poolId);
  const poolPicks = db.pool_picks.filter((p) => p.pool_id === poolId);
  if (poolPicks.length) {
    const mult = Object.fromEntries((LINEUP_FORMATS[pool.lineup_format] || []).map((x) => [x.id, x.mult]));
    const slotOf = Object.fromEntries(db.pool_lineups.filter((l) => l.pool_id === poolId).map((l) => [l.player_id, l.slot]));
    const base = (p) => {
      const src = players.find((x) => x.id === p.player_id);
      if (p.acquired_via === 'free_agent') return Math.round((src?.points || 0) / 30);
      return p.position === 'T' ? Math.round((src?.points || 0) / 4) : Math.max(0, Math.round((src?.points || 40) / 3));
    };
    const rowsP = mem.map((m) => {
      const roster = poolPicks.filter((p) => p.user_id === m.user_id).map((p) => {
        const slot = p.position === 'T' ? 'T' : (slotOf[p.player_id] || null);
        const over = pool.cap_mode === 'penalty' && payroll(poolId, m.user_id) > pool.cap_amount; // over the cap: no double
        const m2 = pool.lineup_mode ? (p.position === 'T' ? 1 : Math.min(mult[slot] ?? 0, over ? 1 : 2)) : 1;
        return { player_id: p.player_id, position: p.position, player_name: p.player_name, team: p.team, pick_number: p.pick_number, slot, points: base(p) * m2 };
      });
      // points a dropped player earned for you stay yours
      const banked = db.pool_transactions.filter((x) => x.pool_id === poolId && x.user_id === m.user_id)
        .reduce((sum, x) => sum + base({ player_id: x.drop_player_id, position: x.position }), 0);
      return { user_id: m.user_id, username: USERNAMES[m.user_id], quit: !!m.quit_at, score: roster.reduce((sum, r) => sum + r.points, 0) + banked, roster };
    });
    if (pool?.winner_id) { const w = rowsP.find((r) => r.user_id === pool.winner_id); if (w) w.score += 50; }
    // preview: pretend a game is on tonight — the first player of each team is scoring live
    if (pool?.status === 'active' && (pool.lineup_mode || pool.free_agency)) rowsP.forEach((r) => { const p = r.roster.find((x) => x.position !== 'T'); if (p) { p.live_points = 2; p.points += 2; r.live_points = 2; r.score += 2; } });
    rowsP.sort((a, b) => b.score - a.score).forEach((r, i) => { r.rank = i + 1; });
    return { standings: rowsP, last_roster_update: iso(-3600000 * 2) };
  }
  // A realistic finished draft: team round (reverse order) then a snake of the best players
  const n = mem.length || 1;
  const skaters = [...players].filter((p) => p.position !== 'T').sort((x, y) => (y.points || 0) - (x.points || 0));
  const nhl = [...players].filter((p) => p.position === 'T').sort((x, y) => (y.points || 0) - (x.points || 0));
  const bySlot = [...mem].sort((x, y) => x.draft_slot - y.draft_slot);
  const rosterOf = {};
  bySlot.forEach((m) => { rosterOf[m.user_id] = []; });
  let pickNo = 1;
  [...bySlot].reverse().forEach((m, i) => {          // team round: last slot first
    const tm = nhl[(i * 3) % Math.max(1, nhl.length)];
    if (tm) rosterOf[m.user_id].push({ player_id: tm.id, position: 'T', player_name: tm.name, team: tm.team, pick_number: null, points: tm.points || 0 }); // teams are not draft picks
  });
  let next = 0;
  for (let r = 0; r < 6; r++) {                       // then players, snake from slot 1
    const order = r % 2 === 0 ? bySlot : [...bySlot].reverse();
    order.forEach((m) => {
      const p = skaters[next++];
      if (!p) return;
      rosterOf[m.user_id].push({ player_id: p.id, position: p.position, player_name: p.name, team: p.team, pick_number: pickNo++, points: Math.max(0, Math.round((p.points || 40) / 3)) });
    });
  }
  void n;
  const rows = mem.map((m) => {
    const roster = rosterOf[m.user_id] || [];
    return { user_id: m.user_id, username: USERNAMES[m.user_id], quit: !!m.quit_at, score: roster.reduce((sum, r) => sum + r.points, 0), roster };
  });
  if (pool?.winner_id) {
    const w = rows.find((r) => r.user_id === pool.winner_id);
    if (w) w.score += 50;
  }
  rows.sort((a, b) => b.score - a.score).forEach((r, i) => { r.rank = i + 1; });
  return { standings: rows, last_roster_update: iso(-3600000 * 2) };
}

// ── Change notifications (stand-in for realtime) ─────────────────────────────
const listeners = new Set();
const notify = () => setTimeout(() => listeners.forEach((cb) => { try { cb(); } catch (e) { /* ignore */ } }), 30);

// ── Query builder ────────────────────────────────────────────────────────────
function withProfiles(table, row) {
  if (table === 'pool_members') return { ...row, profiles: { username: USERNAMES[row.user_id] || '—' } };
  return { ...row };
}

function query(table) {
  const filters = [];
  let orderBy = null;
  let mode = 'many';
  let limitN = null;
  const b = {
    select() { return b; },
    eq(col, val) { filters.push((r) => r[col] === val); return b; },
    neq(col, val) { filters.push((r) => r[col] !== val); return b; },
    in(col, arr) { filters.push((r) => arr.includes(r[col])); return b; },
    is(col, val) { filters.push((r) => (r[col] ?? null) === val); return b; },
    order(col, opts = {}) { orderBy = { col, asc: opts.ascending !== false }; return b; },
    limit(n) { limitN = n; return b; },
    single() { mode = 'single'; return b; },
    maybeSingle() { mode = 'maybe'; return b; },
    then(resolve, reject) {
      try {
        let rows = (db[table] || []).filter((r) => filters.every((f) => f(r))).map((r) => withProfiles(table, r));
        if (orderBy) {
          const { col, asc } = orderBy;
          rows.sort((a, c) => (a[col] > c[col] ? 1 : a[col] < c[col] ? -1 : 0) * (asc ? 1 : -1));
        }
        if (limitN != null) rows = rows.slice(0, limitN);
        let out;
        if (mode === 'single') out = rows[0] ? { data: rows[0], error: null } : { data: null, error: { message: 'Not found (preview)' } };
        else if (mode === 'maybe') out = { data: rows[0] || null, error: null };
        else out = { data: rows, error: null };
        return Promise.resolve(out).then(resolve, reject);
      } catch (e) {
        return Promise.reject(e).then(resolve, reject);
      }
    },
  };
  return b;
}

// ── Draft helpers ────────────────────────────────────────────────────────────
function slotForPick(pickNumber, n, teamRound = false) {
  let k = pickNumber;
  if (teamRound) {
    if (pickNumber <= n) return n - pickNumber + 1;
    k = pickNumber - n;
  }
  const roundIdx = Math.floor((k - 1) / n);
  const pos = (k - 1) % n;
  return roundIdx % 2 === 0 ? pos + 1 : n - pos;
}
const inTeamRound = (pool, n) => !!pool.team_round && pool.current_pick_index <= n;
function commitPick(pool, userId, p) {
  db.pool_picks.push({
    id: newId('pk'), pool_id: pool.id, user_id: userId,
    // teams aren't draft picks: no number; players are numbered from #1 after the team round
    pick_number: pool.team_round
      ? (pool.current_pick_index <= db.pool_members.filter((m) => m.pool_id === pool.id).length ? null : pool.current_pick_index - db.pool_members.filter((m) => m.pool_id === pool.id).length)
      : pool.current_pick_index,
    player_id: p.id, player_name: p.name, team: p.team, position: p.position, cap_hit: capOfId(p.id, p.position),
  });
  const n = db.pool_members.filter((m) => m.pool_id === pool.id).length;
  Object.assign(pool, { pending_pick_player_id: null, pending_pick_player_name: null, pending_pick_team: null, pending_pick_position: null });
  if (pool.current_pick_index >= n * pool.roster_size) {
    pool.status = 'active'; pool.pick_deadline = null;
    if (pool.lineup_mode) initLineups(pool);
  } else {
    pool.current_pick_index += 1;
    pool.pick_deadline = iso(pool.pick_seconds * 1000);
  }
}
function positionFull(pool, userId, position) {
  const limit = { F: pool.slots_f, D: pool.slots_d, G: pool.slots_g, T: pool.slots_t ?? 1 }[position];
  if (limit == null) return false;
  return db.pool_picks.filter((k) => k.pool_id === pool.id && k.user_id === userId && k.position === position).length >= limit;
}
function bestAvailable(poolId, userId) {
  const pool = db.pools.find((p) => p.id === poolId);
  const n = db.pool_members.filter((m) => m.pool_id === poolId).length;
  const teamTime = pool && inTeamRound(pool, n);
  const taken = new Set(db.pool_picks.filter((k) => k.pool_id === poolId).map((k) => k.player_id));
  return [...players].sort((a, b) => (b.points || 0) - (a.points || 0))
    .find((p) => !taken.has(p.id)
      && (teamTime ? p.position === 'T' : p.position !== 'T')
      && !(pool && userId && positionFull(pool, userId, p.position))
      && !(pool && userId && !capFitsFake(pool, userId, p, true)));
}

// Preview-only switches (toolbar): fast bots, and let the preview pick for you too
export const previewOptions = { fast: false, autoMe: false };
export function setPreviewOption(key, value) { previewOptions[key] = value; }

// Bots take their turns a few seconds after it becomes their pick.
setInterval(() => {
  let changed = false;
  db.pools.filter((p) => p.status === 'drafting').forEach((pool) => {
    const mem = db.pool_members.filter((m) => m.pool_id === pool.id);
    const slot = slotForPick(pool.current_pick_index, mem.length, !!pool.team_round);
    const onClock = mem.find((m) => m.draft_slot === slot);
    if (!onClock || (onClock.user_id === PREVIEW_USER_ID && !previewOptions.autoMe)) return;
    const deadline = new Date(pool.pick_deadline).getTime();
    const think = previewOptions.fast ? 1 : 4;
    if (Date.now() < deadline - (pool.pick_seconds - think) * 1000) return; // bots "think" a few seconds
    const p = bestAvailable(pool.id, onClock.user_id);
    if (p) { commitPick(pool, onClock.user_id, p); changed = true; }
  });
  // Reaction challenge: bots get ready / react automatically
  db.pools.forEach((pool) => {
    const mem = db.pool_members.filter((m) => m.pool_id === pool.id);
    if (pool.challenge_state === 'waiting') {
      mem.filter((m) => m.user_id !== PREVIEW_USER_ID && !m.challenge_ready).slice(0, 1).forEach((m) => { m.challenge_ready = true; changed = true; });
      if (mem.every((m) => m.challenge_ready)) {
        pool.challenge_state = 'armed'; pool.challenge_armed_at = iso(1500 + Math.random() * 2500); changed = true;
      }
    } else if (pool.challenge_state === 'armed' && Date.now() > new Date(pool.challenge_armed_at).getTime() + 150) {
      mem.filter((m) => m.user_id !== PREVIEW_USER_ID && m.challenge_reaction_ms == null).slice(0, 1).forEach((m) => {
        m.challenge_reaction_ms = 180 + Math.round(Math.random() * 220); changed = true;
      });
      if (mem.every((m) => m.challenge_reaction_ms != null)) { pool.challenge_state = 'finished'; changed = true; }
    }
  });
  if (changed) notify();
}, 1000);

// ── RPCs ─────────────────────────────────────────────────────────────────────
const ok = (extra = {}) => ({ data: { success: true, ...extra }, error: null });
const fail = (msg) => ({ data: { success: false, error: msg }, error: null });

function rpc(name, a = {}) {
  const pool = db.pools.find((p) => p.id === a.p_pool_id);
  const mem = pool ? db.pool_members.filter((m) => m.pool_id === pool.id) : [];
  const me = mem.find((m) => m.user_id === PREVIEW_USER_ID);
  let res;
  switch (name) {
    case 'server_now': res = { data: new Date().toISOString(), error: null }; break;

    case 'create_pool': {
      const id = newId('p');
      db.pools.push(basePool(id, { name: a.p_name, format: a.p_format, entry_fee: a.p_entry_fee, prize_pool: a.p_entry_fee, max_members: a.p_max_members, roster_size: a.p_roster_size, pick_seconds: a.p_pick_seconds, slots_f: a.p_slots_f ?? null, slots_d: a.p_slots_d ?? null, slots_g: a.p_slots_g ?? null, slots_t: a.p_slots_f != null ? (a.p_slots_t ?? 1) : null, team_round: a.p_slots_f != null, lineup_mode: !!a.p_lineup_format, lineup_format: a.p_lineup_format || null, free_agency: !!a.p_free_agency, fa_pickups_per_month: 2, cap_mode: a.p_cap_mode || null, cap_amount: a.p_cap_mode ? a.p_cap_amount : null, created_at: new Date().toISOString() }));
      db.pool_members.push(member(id, PREVIEW_USER_ID, 1));
      // preview: three fake friends join right away so you can run the whole draft alone
      ['u-habs', 'u-snipe', 'u-luck'].slice(0, Math.max(0, (a.p_max_members || 4) - 1)).forEach((u, i) => db.pool_members.push(member(id, u, i + 2)));
      db.pools.find((p) => p.id === id).prize_pool = a.p_entry_fee * Math.min(4, a.p_max_members || 4);
      res = ok({ pool_id: id }); break;
    }
    case 'join_pool':
      if (me) { res = fail('Already in pool'); break; }
      db.pool_members.push(member(pool.id, PREVIEW_USER_ID, mem.length + 1));
      pool.prize_pool += pool.entry_fee; res = ok(); break;
    case 'leave_pool':
      if (pool.status === 'active') { me.quit_at = new Date().toISOString(); res = ok(); break; }
      db.pool_members = db.pool_members.filter((m) => !(m.pool_id === pool.id && m.user_id === PREVIEW_USER_ID));
      pool.prize_pool = Math.max(0, pool.prize_pool - pool.entry_fee);
      db.pool_members.filter((m) => m.pool_id === pool.id).forEach((m, i) => { m.draft_slot = i + 1; });
      res = ok(); break;
    case 'start_draft':
      Object.assign(pool, { status: 'drafting', current_pick_index: 1, pick_deadline: iso(pool.pick_seconds * 1000) });
      res = ok(); break;
    case 'cancel_pool':
      db.pools = db.pools.filter((p) => p.id !== pool.id);
      db.pool_members = db.pool_members.filter((m) => m.pool_id !== pool.id);
      res = ok(); break;
    case 'settle_pool': {
      const top = fakeStandings(pool.id).standings[0];
      Object.assign(pool, { status: 'completed', winner_id: top?.user_id || null });
      res = ok(); break;
    }
    case 'select_player': {
      if (inTeamRound(pool, mem.length) && a.p_position !== 'T') { res = fail('Team round: pick your NHL team first'); break; }
      if (positionFull(pool, PREVIEW_USER_ID, a.p_position)) { res = fail(`Your ${a.p_position} slots are full`); break; }
      if (!capFitsFake(pool, PREVIEW_USER_ID, { id: a.p_player_id, position: a.p_position })) { res = fail('He doesn’t fit under your cap'); break; }
      Object.assign(pool, { pending_pick_player_id: a.p_player_id, pending_pick_player_name: a.p_player_name, pending_pick_team: a.p_team, pending_pick_position: a.p_position });
      res = ok(); break;
    }
    case 'confirm_pick': {
      const p = { id: pool.pending_pick_player_id, name: pool.pending_pick_player_name, team: pool.pending_pick_team, position: pool.pending_pick_position };
      if (!p.id) { res = fail('No player selected'); break; }
      if (!capFitsFake(pool, PREVIEW_USER_ID, p)) { res = fail('That player can no longer be picked'); break; }
      commitPick(pool, PREVIEW_USER_ID, p); res = ok(); break;
    }
    case 'advance_if_expired': {
      if (pool && pool.status === 'drafting' && new Date(pool.pick_deadline).getTime() <= Date.now()) {
        const slot = slotForPick(pool.current_pick_index, mem.length, !!pool.team_round);
        const onClock = mem.find((m) => m.draft_slot === slot);
        const p = onClock && bestAvailable(pool.id, onClock.user_id);
        if (onClock && p) commitPick(pool, onClock.user_id, p);
      }
      res = ok(); break;
    }
    case 'set_lineup': {
      const cap = Object.fromEntries((LINEUP_FORMATS[pool.lineup_format] || []).map((x) => [x.id, x.cap]));
      const counts = {};
      for (const [pid, slot] of Object.entries(a.p_slots || {})) {
        const pk = db.pool_picks.find((p) => p.pool_id === pool.id && p.user_id === PREVIEW_USER_ID && p.player_id === pid);
        if (!pk) { res = fail('Not your player'); break; }
        if (slot[0] !== pk.position || cap[slot] == null) { res = fail('Wrong slot'); break; }
        counts[slot] = (counts[slot] || 0) + 1;
        if (counts[slot] > cap[slot]) { res = fail(`${slot} is full`); break; }
      }
      if (res) break;
      Object.entries(a.p_slots || {}).forEach(([pid, slot]) => {
        const row = db.pool_lineups.find((l) => l.pool_id === pool.id && l.player_id === pid);
        if (row) row.slot = slot; else db.pool_lineups.push({ pool_id: pool.id, user_id: PREVIEW_USER_ID, player_id: pid, slot });
      });
      res = ok(); break;
    }
    case 'free_agent_swap': {
      if (!pool?.free_agency) { res = fail('Free agency is off in this pool'); break; }
      if (pool.status !== 'active') { res = fail('Free agency opens once the draft is done'); break; }
      const drop = db.pool_picks.find((p) => p.pool_id === pool.id && p.user_id === PREVIEW_USER_ID && p.player_id === a.p_drop_player_id);
      if (!drop) { res = fail('That player is not on your team'); break; }
      if (drop.position === 'T' || a.p_add_position === 'T') { res = fail('Teams cannot be swapped'); break; }
      if (drop.position !== a.p_add_position) { res = fail('Pick up a player at the same position'); break; }
      if (db.pool_picks.some((p) => p.pool_id === pool.id && p.player_id === a.p_add_player_id)) { res = fail('That player is already on a team'); break; }
      if (pickupsLeft(pool, PREVIEW_USER_ID) <= 0) { res = fail('No pickups left this month'); break; }
      if (pool.cap_mode) {
        const before = payroll(pool.id, PREVIEW_USER_ID);
        const after = before - (drop.cap_hit || 0) + capOfId(a.p_add_player_id, a.p_add_position);
        if (after > pool.cap_amount && (before <= pool.cap_amount || after > before)) { res = fail('That pickup puts you over the cap'); break; }
      }
      swapPlayer(pool, PREVIEW_USER_ID, drop, { id: a.p_add_player_id, name: a.p_add_player_name, team: a.p_add_team, position: a.p_add_position });
      res = ok({ pickups_left: pickupsLeft(pool, PREVIEW_USER_ID) }); break;
    }
    case 'set_pool_lineup_mode': {
      if (pool.creator_id !== PREVIEW_USER_ID) { res = fail('Only the host can change the settings'); break; }
      if (pool.status !== 'active') { res = fail('Settings can be changed once the draft is done'); break; }
      if (a.p_enabled) {
        const lines = LINEUP_FORMATS[a.p_format];
        if (!lines) { res = fail('Pick a lineup size'); break; }
        const room = (pos) => lines.filter((x) => x.pos === pos).reduce((n, x) => n + x.cap, 0);
        const most = (pos) => Math.max(0, ...mem.map((m) => db.pool_picks.filter((k) => k.pool_id === pool.id && k.user_id === m.user_id && k.position === pos).length));
        if (['F', 'D', 'G'].some((pos) => most(pos) > room(pos))) { res = fail('That lineup size is too small for the drafted teams'); break; }
      }
      db.pool_setting_changes.push({ id: newId('sc'), pool_id: pool.id, user_id: PREVIEW_USER_ID, setting: 'lineup_mode',
        old_value: pool.lineup_mode ? pool.lineup_format : 'off', new_value: a.p_enabled ? a.p_format : 'off', created_at: new Date().toISOString() });
      pool.per_game = true;
      pool.lineup_mode = !!a.p_enabled;
      if (a.p_enabled) {
        pool.lineup_format = a.p_format;
        db.pool_lineups = db.pool_lineups.filter((l) => l.pool_id !== pool.id);
        initLineups(pool);
      }
      res = ok(); break;
    }
    case 'set_pool_template':
      if (me) me.active_template_id = a.p_template_id; res = ok(); break;

    // Draft-order reaction challenge
    case 'start_order_challenge':
      mem.forEach((m) => { m.challenge_ready = false; m.challenge_reaction_ms = null; });
      pool.challenge_state = 'waiting'; pool.challenge_armed_at = null; res = ok(); break;
    case 'mark_ready': me.challenge_ready = true; res = ok(); break;
    case 'record_reaction': {
      const armed = new Date(pool.challenge_armed_at).getTime();
      me.challenge_reaction_ms = Date.now() < armed ? 99999 : Math.max(1, Date.now() - armed);
      if (mem.every((m) => m.challenge_reaction_ms != null)) pool.challenge_state = 'finished';
      res = ok(); break;
    }
    case 'cancel_order_challenge': pool.challenge_state = 'none'; res = ok(); break;
    case 'apply_order_challenge': {
      [...mem].sort((x, y) => (x.challenge_reaction_ms ?? 99999) - (y.challenge_reaction_ms ?? 99999))
        .forEach((m, i) => { m.draft_slot = i + 1; });
      pool.challenge_state = 'none'; res = ok(); break;
    }

    // Pick lists
    case 'create_queue_template': {
      const id = newId('tpl');
      db.user_queue_templates.push({ id, user_id: PREVIEW_USER_ID, name: a.p_name, created_at: new Date().toISOString() });
      res = ok({ template_id: id }); break;
    }
    case 'rename_queue_template': {
      const t = db.user_queue_templates.find((x) => x.id === a.p_template_id);
      if (t) t.name = a.p_name; res = ok(); break;
    }
    case 'delete_queue_template':
      db.user_queue_templates = db.user_queue_templates.filter((x) => x.id !== a.p_template_id);
      db.user_queue_entries = db.user_queue_entries.filter((x) => x.template_id !== a.p_template_id);
      res = ok(); break;
    case 'add_to_queue': {
      const list = db.user_queue_entries.filter((x) => x.template_id === a.p_template_id);
      db.user_queue_entries.push({ id: newId('qe'), template_id: a.p_template_id, player_id: a.p_player_id, player_name: a.p_player_name, team: a.p_team, position: a.p_position, rank: list.length + 1 });
      res = ok(); break;
    }
    case 'remove_from_queue': {
      const e = db.user_queue_entries.find((x) => x.id === a.p_entry_id);
      db.user_queue_entries = db.user_queue_entries.filter((x) => x.id !== a.p_entry_id);
      if (e) db.user_queue_entries.filter((x) => x.template_id === e.template_id).sort((x, y) => x.rank - y.rank).forEach((x, i) => { x.rank = i + 1; });
      res = ok(); break;
    }
    case 'reorder_queue':
      (a.p_ordered_ids || []).forEach((id, i) => { const e = db.user_queue_entries.find((x) => x.id === id); if (e) e.rank = i + 1; });
      res = ok(); break;

    default:
      res = fail(`"${name}" is not simulated in the preview`);
  }
  notify();
  return Promise.resolve(res);
}

// ── Public client ────────────────────────────────────────────────────────────
export function createFakeSupabase() {
  return {
    from: (table) => query(table),
    rpc,
    channel() {
      const cbs = [];
      const ch = {
        on(_evt, _filter, cb) { if (cb) cbs.push(cb); return ch; },
        subscribe() { cbs.forEach((cb) => listeners.add(cb)); return ch; },
        _cbs: cbs,
      };
      return ch;
    },
    removeChannel(ch) { (ch?._cbs || []).forEach((cb) => listeners.delete(cb)); },
    auth: {
      getSession: async () => ({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe() {} } } }),
    },
    __isPreview: true,
  };
}
